import * as React from 'react';
import { Building2, ChevronDown, ChevronRight, GraduationCap, User } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { SESSION_TYPE_CONFIG } from '@/components/pairs/pair-configs';
import type { SessionForMatching } from '@/types/inperson/matching.types';
import { deriveMatchingSessionHeader } from '../matching-utils';

/** Props for {@link MatchingRow}. */
interface MatchingRowProps {
  session: SessionForMatching;
  isExpanded: boolean;
  onToggle: () => void;
  tutorCount: number;
  studentCount: number;
  maxStudents: number | null;
}

/** Table row for an in-person matching session. */
export function MatchingRow({
  session,
  isExpanded,
  onToggle,
  tutorCount,
  studentCount,
  maxStudents,
}: Readonly<MatchingRowProps>) {
  const { dateLabel, timeLabel, establishmentDisplay } = deriveMatchingSessionHeader(session);
  const typeConfig = SESSION_TYPE_CONFIG[session.type];
  const TypeIcon = typeConfig?.icon;
  const maxTutors = session.activity?.maxStudentsPerTutor;

  function renderChevron(): React.ReactNode {
    if (isExpanded) return <ChevronDown className="w-4 h-4 text-[var(--color-1-nuance-1)]" />;
    return <ChevronRight className="w-4 h-4 text-[var(--color-1-nuance-1)]" />;
  }

  function renderTypeBadge(): React.ReactNode {
    return (
      <LabelBadge
        label={typeConfig?.label ?? session.type}
        icon={TypeIcon}
        variant={typeConfig?.badgeVariant ?? 'ghost'}
        color={typeConfig?.badgeColor ?? 'var(--color-2-nuance-1)'}
        size="sm"
      />
    );
  }

  function renderTutorQuota(): React.ReactNode {
    if (maxTutors == null) return null;
    return (
      <>
        <span className="text-xs font-normal">/</span>
        <span className="text-xs font-normal">{maxTutors}</span>
      </>
    );
  }

  function renderStudentQuota(): React.ReactNode {
    if (maxStudents == null) return null;
    return (
      <>
        <span className="text-xs font-normal">/</span>
        <span className="text-xs font-normal">{maxStudents}</span>
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className="matching-collapse-header"
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="matching-collapse-chevron">{renderChevron()}</div>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex min-w-0 flex-1 basis-0 flex-col justify-center gap-0.5">
            <p className="font-medium text-base" style={{ color: 'var(--color-1-nuance-1)' }}>
              {dateLabel}
            </p>
            <p className="text-sm text-muted-foreground tabular-nums">{timeLabel}</p>
          </div>
          <div className="flex min-w-0 flex-1 basis-0 items-center justify-start">
            {renderTypeBadge()}
          </div>
          <div className="flex min-w-0 flex-1 basis-0 items-center gap-2">
            <Building2 className="w-4 h-4 shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
            <span className="min-w-0 truncate text-sm text-foreground">
              {establishmentDisplay || 'Non défini'}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <div
              className="flex items-center justify-center gap-1 px-2 py-1 rounded bg-[color-mix(in_srgb,var(--color-2-nuance-1)_10%,transparent)] text-[var(--color-2-nuance-1)] border border-[var(--color-2-nuance-1)]"
              style={{ width: '120px' }}
            >
              <GraduationCap className="w-3 h-3 shrink-0 text-[var(--color-1-nuance-1)]" />
              <span className="text-[9px] uppercase tracking-wide">tuteurs :</span>
              <span className="text-xs font-bold">{tutorCount}</span>
              {renderTutorQuota()}
            </div>
            <div
              className="flex items-center justify-center gap-1 px-2 py-1 rounded bg-[color-mix(in_srgb,var(--color-3-nuance-1)_10%,transparent)] text-[var(--color-3-nuance-1)] border border-[var(--color-3-nuance-1)]"
              style={{ width: '120px' }}
            >
              <User className="w-3 h-3 shrink-0 text-[var(--color-1-nuance-1)]" />
              <span className="text-[9px] uppercase tracking-wide">Élèves :</span>
              <span className="text-xs font-bold">{studentCount}</span>
              {renderStudentQuota()}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
